import * as React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import HomeIcon from '@mui/icons-material/Home';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate, useLocation } from 'react-router';
import ProfileModal from '../../../views/home/components/profileModal';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { useMediaQuery, useTheme, Tooltip } from '@mui/material';
import EventsApi from '../../../api/eventsApi/eventsApi';
import { reportUi } from '../../../views/reports/reportUiTheme';

/** Tailwind w-56 */
export const SIDEBAR_WIDTH_EXPANDED = 224;
export const SIDEBAR_WIDTH_COMPACT = 64;

const ICON_SX = { fontSize: 18 } as const;

function isRouteActive(link: string | undefined, pathname: string): boolean {
  if (!link) return false;
  if (link === '/') return pathname === '/' || pathname === '/home';
  return pathname === link || pathname.startsWith(`${link}/`);
}

type MenuItem = {
  title: string;
  icon: React.ReactElement;
  link?: string;
  action?: () => void;
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profileModalOpen, setProfileModalOpen] = React.useState(false);
  const [canAccessAdmin, setCanAccessAdmin] = React.useState(false);

  const theme = useTheme();
  const isCompact = useMediaQuery(theme.breakpoints.down('md'));

  React.useEffect(() => {
    const checkAdminAccess = async () => {
      try {
        const user = await EventsApi.getUser();
        const superadminClaimId = '00000000-0000-0000-0000-000000000001';
        const adminClaimId = '00000000-0000-0000-0000-000000000002';
        setCanAccessAdmin(
          user.operationClaimIds?.includes(superadminClaimId) ||
            user.operationClaimIds?.includes(adminClaimId) ||
            false
        );
      } catch (error) {
        console.error('Error checking admin access:', error);
        setCanAccessAdmin(false);
      }
    };
    checkAdminAccess();
  }, []);

  const menuItems: MenuItem[] = [
    { title: 'Anasayfa', icon: <HomeIcon sx={ICON_SX} />, link: '/' },
    { title: 'Analizler', icon: <AnalyticsIcon sx={ICON_SX} />, link: '/analysis' },
    { title: 'Tesislerim', icon: <AssignmentIcon sx={ICON_SX} />, link: '/reports' },
    ...(canAccessAdmin
      ? [{ title: 'Admin Paneli', icon: <AdminPanelSettingsIcon sx={ICON_SX} />, link: '/admin' } as MenuItem]
      : []),
    { title: 'Ayarlar', icon: <SettingsIcon sx={ICON_SX} />, action: () => setProfileModalOpen(true) },
  ];

  const navItemSx = (active: boolean) => ({
    display: 'flex',
    alignItems: 'center',
    gap: 1.5,
    px: 2,
    py: 1.25,
    borderRadius: reportUi.radius,
    fontSize: '0.875rem',
    fontWeight: active ? 600 : 500,
    color: active ? reportUi.primary : reportUi.textMain,
    bgcolor: active ? reportUi.surfaceSoft : 'transparent',
    border: 'none',
    cursor: 'pointer',
    width: '100%',
    textAlign: 'left' as const,
    fontFamily: 'inherit',
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': {
      bgcolor: reportUi.surfaceSoft,
      color: reportUi.primary,
    },
  });

  const sidebarWidth = isCompact ? SIDEBAR_WIDTH_COMPACT : SIDEBAR_WIDTH_EXPANDED;

  return (
    <Box
      component="aside"
      sx={{
        width: sidebarWidth,
        height: '100vh',
        borderRight: `1px solid ${reportUi.border}`,
        display: 'flex',
        flexDirection: 'column',
        bgcolor: reportUi.surface,
        position: 'fixed',
        left: 0,
        top: 0,
        zIndex: 1000,
        transition: 'width 0.3s ease',
      }}
    >
      {/* Marka — önceki sidebar: düz logo görseli + Conthrax başlık */}
      <Box
        sx={{
          borderBottom: `1px solid ${reportUi.border}`,
        }}
      >
        {isCompact ? (
          <Tooltip title="INT-TECH" placement="right">
            <Box
              sx={{
                minHeight: 48,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: 1,
              }}
            >
              <Box
                component="img"
                src="/logo_inttech.png"
                alt="Int-Tech Logo"
                sx={{ width: 24, height: 24, objectFit: 'contain', display: 'block' }}
              />
            </Box>
          </Tooltip>
        ) : (
          <Box
            sx={{
              minHeight: 64,
              display: 'flex',
              alignItems: 'center',
              px: 2,
              gap: 1.5,
            }}
          >
            <Box
              component="img"
              src="/logo_inttech.png"
              alt="Int-Tech Logo"
              sx={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0, display: 'block' }}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontFamily: 'Conthrax, monospace',
                fontWeight: 700,
                letterSpacing: '.1rem',
                color: reportUi.primary,
                lineHeight: 1.2,
              }}
            >
              INT-TECH
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        component="nav"
        sx={{
          mt: 0.5,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          px: 1.5,
          overflowY: 'auto',
        }}
      >
        {menuItems.map((item) => {
          const active = item.link ? isRouteActive(item.link, location.pathname) : false;
          const onActivate = () => {
            if (item.action) item.action();
            else if (item.link) navigate(item.link);
          };

          if (isCompact) {
            return (
              <Tooltip key={item.title} title={item.title} placement="right">
                <Box
                  component="button"
                  type="button"
                  onClick={onActivate}
                  sx={{
                    ...navItemSx(active),
                    justifyContent: 'center',
                    px: 0,
                    minHeight: 44,
                  }}
                >
                  {item.icon}
                </Box>
              </Tooltip>
            );
          }

          return (
            <Box
              key={item.title}
              component="button"
              type="button"
              onClick={onActivate}
              sx={navItemSx(active)}
            >
              {item.icon}
              {item.title}
            </Box>
          );
        })}
      </Box>

      <Box sx={{ px: 1.5, pb: 2, pt: 0 }}>
        {isCompact ? (
          <Tooltip title="Çıkış" placement="right">
            <Box
              component="button"
              type="button"
              onClick={() => navigate('/logout')}
              sx={{
                ...navItemSx(false),
                justifyContent: 'center',
                px: 0,
                minHeight: 44,
                color: `${reportUi.textMuted} !important`,
                '&:hover': {
                  bgcolor: reportUi.surfaceSoft,
                  color: `${reportUi.textMain} !important`,
                },
              }}
            >
              <LogoutIcon sx={ICON_SX} />
            </Box>
          </Tooltip>
        ) : (
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/logout')}
            sx={{
              ...navItemSx(false),
              color: `${reportUi.textMuted} !important`,
              '&:hover': {
                bgcolor: reportUi.surfaceSoft,
                color: `${reportUi.textMain} !important`,
              },
            }}
          >
            <LogoutIcon sx={ICON_SX} />
            Çıkış
          </Box>
        )}
      </Box>

      <ProfileModal open={profileModalOpen} onClose={() => setProfileModalOpen(false)} />
    </Box>
  );
};

export default Sidebar;
